<script setup>
import { ref, onMounted } from 'vue';
import apiRequest from '@/service/api/ApiRequestService';
import { useShepherd } from 'vue-shepherd';
import { useLayout } from '@/layout/composables/layout';

import 'shepherd.js/dist/css/shepherd.css';

import AppMenuItem from './AppMenuItem.vue';

const model = ref([
    {
        items: [
            { label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/' },
            { label: 'Reports', icon: 'pi pi-fw pi-chart-bar', to: '/dashboard/reports' },
            { label: 'Campaigns', icon: 'pi pi-fw pi-calendar', to: '/dashboard/campaigns', children: ['campaign/*'] },
            { label: 'Creatives', icon: 'pi pi-fw pi-images', to: '/dashboard/creatives', tag: 'New' },
            { label: 'Invoices', icon: 'pi pi-fw pi-file', to: '/dashboard/invoices', children: ['campaign/*'] },
            { label: 'Offers', icon: 'pi pi-fw pi-dollar', to: '/dashboard/offers' },
            {
                label: 'Case Study',
                icon: 'pi pi-fw pi-search',
                to: '/dashboard/case-study',
                children: ['caseStudy/*'],
                tag: 'New'
            }, 
            {
                label: 'Information',
                icon: 'pi pi-fw pi-info-circle',
                to: '/dashboard/information',
                children: ['campaign/*'],
                tag: 'New'
            },
        ]
    }
]);

const { layoutConfig } = useLayout();

const appVersion = ref('v0.4.1-beta');

const versionNumber = ref(null);

const tour = useShepherd({
    useModalOverlay: true
});

onMounted(() => {
    //check if version is acknowledged
    // if (localStorage.viewedVersion !== appVersion.value) {
    //     //add class darkedMode to body
    //     if (layoutConfig.darkTheme.value) {
    //         document.body.classList.add('darkedMode');
    //     }
    //     setTimeout(() => {
    //         startTour();
    //     }, 3000);
    //     localStorage.setItem('viewedVersion', appVersion.value);
    // }
});

const releaseLogVisible = ref(false);
const loadingReleaseLog = ref(false);
const releaseNotes = ref([]);
const totalReleaseNoteCount = ref(1);

const openReleaseNotes = () => {
    return;
    fetchReleaseNotes(0, true);
    releaseLogVisible.value = true;
    if (tour.isActive()) {
        tour.cancel();
    }
};

const fetchReleaseNotes = (offset = 0, firstLoad = false) => {
    loadingReleaseLog.value = true;
    //get release notes
    apiRequest.get('dashboard/release-notes?offset=' + offset + '&firstLoad=' + firstLoad).then((response) => {
        releaseNotes.value = response.data;
        loadingReleaseLog.value = false;
        if (response.totalRecords) {
            totalReleaseNoteCount.value = response.totalRecords;
        }
    });
};
const onPaginateReleaseNotes = (event) => {
    loadingReleaseLog.value = true;
    fetchReleaseNotes(event.page);
};

const startTour = () => {
    tour.addStep({
        attachTo: { element: versionNumber.value, on: 'top' },
        title: 'A new version has been released!',
        text: 'We have released a new version to improve your experience. Click here to view the release notes.'
    });

    tour.start();
};
</script>

<template>
    <ul class="layout-menu">
        <template v-for="(item, i) in model" :key="item">
            <app-menu-item v-if="!item.separator" :item="item" :index="i"></app-menu-item>
            <li v-if="item.separator" class="menu-separator"></li>
        </template>
    </ul>
    <div class="appVersionInfo cursor-pointer" @click="openReleaseNotes" ref="versionNumber">
        <small>{{ appVersion }}</small>
    </div>

    <Sidebar v-model:visible="releaseLogVisible" header="Sidebar" class="w40rem" position="left" :dismissable="true">
        <h3 class="mb-5">Release Notes</h3>
        <div v-if="loadingReleaseLog" class="mt-5">
            <LoadingSpinner :size="45" />
        </div>
        <div v-else>
            <div class="releaseNotesContainer">
                <div class="card mb-3" v-for="(release, i) in releaseNotes" :key="i">
                    <h5 v-if="release.title" class="mb-3">
                        <b>{{ release.title }}</b>
                    </h5>
                    <div class="flex justify-content-between align-items-center">
                        <div>
                            <Tag v-for="(tagName, tagI) in release.tags" :key="tagI" :value="tagName"
                                :severity="tagName == 'Bugfix' ? 'danger' : tagName == 'Improvement' ? 'warning' : tagName == 'Announcement' ? 'info' : 'success'"
                                class="mr-2"></Tag>
                        </div>
                        <div>
                            <Tag severity="success" :value="'v' + release.version"></Tag>
                        </div>
                    </div>
                    <div class="mt-4" v-html="release.data.notes"></div>
                </div>
            </div>

            <Paginator v-if="totalReleaseNoteCount > 10" :rows="10" :totalRecords="totalReleaseNoteCount"
                @page="onPaginateReleaseNotes"></Paginator>
        </div>
    </Sidebar>
</template>

<style lang="scss" scoped>
.appVersionInfo {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    text-align: center;
    padding: 10px;
    background-color: var(--surface-hover);
    color: var(--text-color);
    font-size: 10px;
    box-shadow: 0px -1px 5px 0px var(--shadow);
}
</style>

<style>
.w40rem {
    width: 40rem;
}

.releaseNotesContainer {
    max-height: 72vh;
    overflow-y: auto;
}

.darkedMode .shepherd-text,
.darkedMode .shepherd-title {
    color: #ffffff;
}

.darkedMode .shepherd-text {
    background: #1f2937;
}

.darkedMode .shepherd-has-title .shepherd-content .shepherd-header {
    background: #374151;
}

.darkedMode .shepherd-arrow:before {
    background: #9097d4;
}
</style>
